/* @flow */

import './CardModal.css';
import clsx from 'clsx';
import { produce } from 'immer';
import * as React from 'react';
import { connect } from 'react-redux';

import type { GamestreamToken } from '../../../helpers/gaming/types';
import type { Undefined } from '../../../helpers/jsHelpers/types';
import type { NETGEM_API_V8_RAW_FEED } from '../../../libs/netgemLibrary/v8/types/FeedResult';
import type { NETGEM_API_V8_METADATA_SCHEDULE } from '../../../libs/netgemLibrary/v8/types/MetadataSchedule';
import type { NETGEM_API_V8_URL_DEFINITION } from '../../../libs/netgemLibrary/v8/types/NtgVideoFeed';
import type { NETGEM_API_V8_PURCHASE_INFO } from '../../../libs/netgemLibrary/v8/types/PurchaseInfo';
import type { NETGEM_API_V8_REQUEST_RESPONSE } from '../../../libs/netgemLibrary/v8/types/RequestResponse';
import type { ModalState } from '../../../redux/modal/reducers';
import type { CombinedReducers } from '../../../redux/reducers';
import type { Dispatch } from '../../../redux/types/types';
import type { CONFIRMATION_DATA_MODAL_TYPE } from '../confirmationModal/ConfirmationModal';
import type { EULA_DATA_MODAL_TYPE } from '../eulaModal/EulaModal';
import type { GAME_PROFILE_SELECT_DATA_MODAL_TYPE } from '../gameProfileSelectModal/GameProfileSelectModal';
import type { MESSAGE_MODAL_TYPE } from '../messageModal/MessageModal';
import type { CloseCallbackType } from '../vodPurchaseModal/VodPurchaseModal';

import { APPLICATION_ID } from '../../../helpers/applicationCustomization/types';
import { getChannelName } from '../../../helpers/channel/helper';
import { channelHasStartover, getChannelImageId } from '../../../helpers/channel/helper';
import AccurateTimestamp from '../../../helpers/dateTime/AccurateTimestamp';
import { MILLISECONDS_PER_HOUR, formatDate, formatExpirationDate } from '../../../helpers/dateTime/Format';
import { logError, logWarning, showDebug } from '../../../helpers/debug/debug';
import GameManager from '../../../helpers/gaming/gameManager';
import { addLinkedData } from '../../../helpers/jsHelpers/linkedData';
import { parseBoolean } from '../../../helpers/jsHelpers/parser';
import { fireAndForget } from '../../../helpers/jsHelpers/promise';
import { LoadableStatus } from '../../../helpers/loadable/loadable';
import Localizer from '../../../helpers/localization';
import { Messenger, MessengerEvents } from '../../../helpers/messenger';
import { getEpisodeAndSeriesRecordStatus, getScheduledRecordingIdFromRecordingId } from '../../../helpers/npvr/recording';
import { type ParentalControlItemState, ParentalControlLockReason, ParentalControlLockState } from '../../../helpers/parentalControl/constsAndTypes';
import { showUnavailableContentModal } from '../../../helpers/parentalControl/helpers';
import ParentalControlManager from '../../../helpers/parentalControl/parentalControlManager';
import PlayingHistoryCache from '../../../helpers/playingHistory/PlayingHistoryCache';
import {
  type ADD_TO_FAVORITE_DATA_TYPE,
  type ADD_TO_WISHLIST_DATA_TYPE,
  type BASE_VOD_PURCHASE_DATA_TYPE,
  PendingOperationKind,
  PendingOperationReason,
  type VOD_PURCHASE_DATA_TYPE,
  clearPendingOperation,
  storePendingOperation,
} from '../../../helpers/rights/pendingOperations';
import { isPlaybackGranted } from '../../../helpers/rights/playback';
import { isFavoriteGranted, isWishlistGranted } from '../../../helpers/rights/wishlist';
import { IMAGE_HEIGHT, IMAGE_WIDTH } from '../../../helpers/ui/constants';
import { getDistributorId } from '../../../helpers/ui/item/distributor';
import { isVodItem } from '../../../helpers/ui/item/metadata';
import { ExtendedItemType } from '../../../helpers/ui/item/types';
import { BroadcastStatus, WebAppHelpersLocationStatus, getBroadcastStatus, getLocationStatus } from '../../../helpers/ui/location/Format';
import { loadEpisodesVodLocationMetadata } from '../../../helpers/ui/metadata/batchOperations';
import { filterCredits } from '../../../helpers/ui/metadata/Exclusion';
import { getEpisodeIndexAndTitle, getStartAndEndTimes, getSynopsis, getTagline, getTitle, renderProgramDetails } from '../../../helpers/ui/metadata/Format';
import { IMAGE_TAG_NO_TEXT, findLandscapeImageId } from '../../../helpers/ui/metadata/image';
import { PROGRAM_INFO } from '../../../helpers/ui/metadata/Types';
import { areViewingHistoryMapsDifferent } from '../../../helpers/ui/section/comparisons';
import { Theme, type ThemeType } from '../../../helpers/ui/theme';
import { BOVodAssetStatus, getTitIdFromProviderInfo, getVodKind, getVodLocationFromVtiId, getVodStatusFromLocations, hasBeenPurchased, hasTrailer } from '../../../helpers/videofutur/metadata';
import { isPackPurchased } from '../../../helpers/videofutur/pack';
import ContinuousWatching from '../../../helpers/viewingHistory/ContinuousWatching';
import WishlistCache from '../../../helpers/wishlist/WishlistCache';
import { ignoreIfAborted } from '../../../libs/netgemLibrary/helpers/cancellablePromise/promiseHelper';
import { getStartOverItem } from '../../../libs/netgemLibrary/v8/helpers/CatchupForAsset';
import { isItemLiveOrAboutToStart } from '../../../libs/netgemLibrary/v8/helpers/Feed';
import { getItemContentType } from '../../../libs/netgemLibrary/v8/helpers/Item';
import { ItemContent, ItemLocationKind, ItemType, type NETGEM_API_V8_FEED_ITEM, type NETGEM_API_V8_ITEM_LOCATION, VodKind } from '../../../libs/netgemLibrary/v8/types/FeedItem';
import {
  METADATA_KIND_PROGRAM,
  METADATA_KIND_SERIES,
  type MetadataKind,
  type NETGEM_API_V8_METADATA,
  type NETGEM_API_V8_METADATA_IMAGE,
  type NETGEM_API_V8_METADATA_PROGRAM,
  type NETGEM_API_V8_METADATA_SERIES,
} from '../../../libs/netgemLibrary/v8/types/MetadataProgram';
import { RecordingOutcome, ScheduledRecordingsKind, type ScheduledRecordingsKindType } from '../../../libs/netgemLibrary/v8/types/Npvr';
import { type NETGEM_API_PLAYINGHISTORY_ITEM, playingHistoryArrayToObject } from '../../../libs/netgemLibrary/v8/types/PlayingHistory';
import { type NETGEM_API_V8_AUTHENT_REALM, NETGEM_API_V8_AUTHENT_REALM_VIDEOFUTUR } from '../../../libs/netgemLibrary/v8/types/Realm';
import { TileConfigTileType } from '../../../libs/netgemLibrary/v8/types/WidgetConfig';
import { FEATURE_NPVR, FEATURE_SUBSCRIPTION } from '../../../redux/appConf/constants';
import { RegistrationType } from '../../../redux/appRegistration/types/types';
import { hideModal, reopenModalCard, showConfirmationModal, showEulaModal, showGameProfileSelectModal, showMessageModal, showPinModal, showVodPurchaseModal } from '../../../redux/modal/actions';
import { getSettingValueByName } from '../../../redux/netgemApi/actions/helpers/settings';
import { updatePlayingHistory } from '../../../redux/netgemApi/actions/personalData/playingHistory';
import { addToWishlist, removeFromWishlist } from '../../../redux/netgemApi/actions/personalData/wishlist';
import sendV8LocationCatchupForAssetRequest from '../../../redux/netgemApi/actions/v8/catchupForAsset';
import getGamestreamToken from '../../../redux/netgemApi/actions/v8/getGamestreamToken';
import sendV8MetadataRequest from '../../../redux/netgemApi/actions/v8/metadata';
import { getImageSourceUrl, getImageUrl } from '../../../redux/netgemApi/actions/v8/metadataImage';
import sendV8MetadataLocationRequest, { getVodLocations } from '../../../redux/netgemApi/actions/v8/metadataLocation';
import { getPurchaseInfoPerAsset } from '../../../redux/netgemApi/actions/v8/purchaseInfo';
import { sendV8RecordingsMetadataRequest } from '../../../redux/netgemApi/actions/v8/recordings';
import addToFavoriteList from '../../../redux/netgemApi/actions/videofutur/addFavorite';
import removeFromFavoriteList from '../../../redux/netgemApi/actions/videofutur/deleteFavorite';
import { resetSectionPageIndices } from '../../../redux/ui/actions';
import { getGameAlias, searchMostRecentProfileForGame } from '../../avenue/item/helper';
import ButtonFX from '../../buttons/ButtonFX';
import CreditsView from '../../credits/Credits';
import { PictoBookmark, PictoBookmarkEmpty, PictoClock, PictoFailure, PictoHeartEmpty, PictoHeartFull, PictoLock, PictoRecord, PictoWarning } from '../../pictos/Element';
import PricingVod, { ContextKind as PricingContext } from '../../pricingVod/PricingVod';
import RecordButton, { DisplayMode, ContextKind as RecordContext } from '../../recordButton/RecordButton';
import StatusPicto from '../../statusPicto/StatusPicto';
import Modal from '../modal';
import { type PIN_DATA_MODAL_TYPE, PinModalKind } from '../pinModal/PinModalConstsAndTypes';
import CardAvenue from './CardModalAvenue';
import {
  type CARD_DATA_MODAL_TYPE,
  type CardModalStateType,
  type CompleteCardModalPropType,
  ImageDisplayType,
  REFRESH_TIMEOUT,
  type ReduxCardModalDispatchToPropsType,
  type ReduxCardModalReducerStateType,
  UNINITIALIZED_LOCK_STATE_DELAY,
} from './CardModalConstsAndTypes';
import GameSlideshow from './GameSlideshow';
import { getDisplayType, getLiveProgress, getWatchingStatus, isFavorite, isItemPlayable, updateTitle } from './helper';

const InitialState = Object.freeze({
  authority: undefined,
  backgroundImageUrl: null,
  broadcastStatus: BroadcastStatus.Unknown,
  canBePlayed: false,
  channelImageUrl: null,
  contentType: ItemContent.Unknown,
  continuousWatching: null,
  followedPictoClassName: '',
  gameLocationMetadata: null,
  imageDisplayType: ImageDisplayType.Unset,
  isDeletable: false,
  isFollowed: null,
  isLiveRecording: false,
  isSeries: false,
  isWaitingForDeleteConfirmation: false,
  isWaitingForRecordConfirmation: false,
  parentalControlState: {
    hasPinCode: false,
    lockReason: undefined,
    lockState: ParentalControlLockState.NotInitialized,
  } as ParentalControlItemState,
  portraitImageUrl: null,
  previewCatchupScheduledEventId: null,
  programMetadata: null,
  purchaseInfo: null,
  seriesMetadata: null,
  slideshowImages: [] as Array<string>,
  startoverItem: null,
  tvLocationMetadata: null,
  vodLocationsMetadata: null,
  vodStatus: null,
});

class CardModalView extends React.PureComponent<CompleteCardModalPropType, CardModalStateType> {
  abortController: AbortController;

  broadcastStatusRefreshTimer: TimeoutID | null;

  failedRecordingId: ?string;

  isInitializingContinuousWatching: boolean;

  recordOutcome: ?RecordingOutcome;

  slider: HTMLElement | null;

  uninitializedLockStateTimer: TimeoutID | null;

  warningScheduledEventId: ?string;

  warningScheduledRecordingId: ?string;

  constructor(props: CompleteCardModalPropType) {
    super(props);

    this.abortController = new AbortController();
    this.failedRecordingId = null;
    this.recordOutcome = null;
    this.broadcastStatusRefreshTimer = null;
    this.isInitializingContinuousWatching = false;
    this.slider = null;
    this.uninitializedLockStateTimer = null;
    this.warningScheduledEventId = null;
    this.warningScheduledRecordingId = null;

    const { cardData } = props;
    if (cardData) {
      const {
        item: { type },
        seriesMetadata,
      } = cardData;
      // When opened from player, only display episode card (not series card)

      const isSeries = type === ItemType.Series || (seriesMetadata !== undefined && seriesMetadata !== null);

      this.state = {
        ...InitialState,
        isSeries,
      };
    } else {
      this.state = { ...InitialState };
    }
  }

  async componentDidMount() {
    const { isSeries } = this.state;

    Messenger.on(MessengerEvents.EPISODES_FEED_LOADED, this.loadVodData);
    Messenger.on(MessengerEvents.CONTINUOUS_WATCHING_PLAY_FROM_EPISODE, this.playSeriesFromEpisode);
    Messenger.on(MessengerEvents.PARENTAL_CONTROL_STATE_UPDATE, this.updateLockState);
    Messenger.on(MessengerEvents.PARENTAL_CONTROL_PIN_UPDATE, this.updateLockState);

    if (isSeries) {
      await this.initializeContinuousWatching();
    }

    this.initialize();
  }

  async componentDidUpdate(prevProps: CompleteCardModalPropType, prevState: CardModalStateType) {
    const { cardData, favoriteList, localResetSectionPageIndices, npvrRecordingsList, npvrScheduledRecordingsList, purchaseList, viewingHistory, viewingHistoryStatus, wishlist, wishlistStatus } =
      this.props;
    const {
      cardData: prevCardData,
      favoriteList: prevFavoriteList,
      npvrRecordingsList: prevNpvrRecordingsList,
      npvrScheduledRecordingsList: prevNpvrScheduledRecordingsList,
      purchaseList: prevPurchaseList,
      viewingHistory: prevViewingHistory,
      wishlist: prevWishlist,
      wishlistStatus: prevWishlistStatus,
    } = prevProps;
    const { broadcastStatus, contentType, isFollowed, isSeries, previewCatchupScheduledEventId, vodLocationsMetadata } = this.state;
    const {
      broadcastStatus: prevBroadcastStatus,
      contentType: prevContentType,
      isFollowed: prevIsFollowed,
      previewCatchupScheduledEventId: prevPreviewCatchupScheduledEventId,
      vodLocationsMetadata: prevVodLocationsMetadata,
    } = prevState;
    const { broadcastStatusRefreshTimer } = this;

    if (!cardData) {
      return;
    }

    if (isSeries && viewingHistoryStatus === LoadableStatus.Loaded && areViewingHistoryMapsDifferent(viewingHistory, prevViewingHistory)) {
      await this.initializeContinuousWatching();
    }

    if (vodLocationsMetadata !== prevVodLocationsMetadata || purchaseList !== prevPurchaseList) {
      this.updateVodStatus();
    }

    if ((!prevPurchaseList && purchaseList) || (!prevFavoriteList && favoriteList) || (wishlistStatus === LoadableStatus.Loaded && prevWishlistStatus !== LoadableStatus.Loaded)) {
      this.checkPendingOperation();
    }

    if (favoriteList !== prevFavoriteList) {
      // VOD/SVOD case
      this.updateIsFollowed();
    }

    if (isFollowed !== prevIsFollowed) {
      this.updateFollowedPictoClassName(prevIsFollowed, isFollowed);
    }

    if (contentType !== prevContentType) {
      if (!broadcastStatusRefreshTimer) {
        this.updateBroadcastStatus();
      }
    }

    const {
      item: { locType, selectedLocation, selectedProgramId, type },
    } = cardData;

    if (!prevCardData) {
      // No card was loaded before (not sure if this could happen...)
      this.updateContentType();
    } else {
      const {
        item: { locType: prevLocType, selectedLocation: prevSelectedLocation, selectedProgramId: prevSelectedProgramId, type: prevType },
      } = prevCardData;

      if (locType !== prevLocType) {
        this.updateBroadcastStatus();
      }

      if (locType !== prevLocType || type !== prevType || selectedProgramId !== prevSelectedProgramId || selectedLocation !== prevSelectedLocation) {
        // Reset saved positions for all sections in the card (using "card_" prefix)
        localResetSectionPageIndices('card_');

        const { slider } = this;
        if (slider) {
          // Scroll to top when card is updated
          slider.scrollTop = 0;
        }

        this.initialize();
        return;
      }

      if (
        npvrRecordingsList !== prevNpvrRecordingsList ||
        npvrScheduledRecordingsList !== prevNpvrScheduledRecordingsList ||
        wishlist !== prevWishlist ||
        broadcastStatus !== prevBroadcastStatus ||
        previewCatchupScheduledEventId !== prevPreviewCatchupScheduledEventId
      ) {
        this.updateCard();
      }
    }
  }

  componentWillUnmount() {
    const { applicationName } = this.props;
    const { abortController } = this;

    abortController.abort('Component CardModal will unmount');

    Messenger.off(MessengerEvents.EPISODES_FEED_LOADED, this.loadVodData);
    Messenger.off(MessengerEvents.CONTINUOUS_WATCHING_PLAY_FROM_EPISODE, this.playSeriesFromEpisode);
    Messenger.off(MessengerEvents.PARENTAL_CONTROL_STATE_UPDATE, this.updateLockState);
    Messenger.off(MessengerEvents.PARENTAL_CONTROL_PIN_UPDATE, this.updateLockState);

    this.resetBroadcastStatusRefreshTimer();
    this.resetUninitializedLockStateTimer();

    updateTitle(applicationName);
  }

  initialize = () => {
    const { cardData } = this.props;
    const { isSeries } = this.state;

    if (!cardData) {
      return;
    }

    const {
      item: { selectedProgramId, seriesId },
      programMetadata,
      seriesMetadata,
    } = cardData;

    if (programMetadata) {
      this.updateContentType();
    } else {
      this.loadProgramMetadata(selectedProgramId);
    }

    if (isSeries && seriesId && !seriesMetadata) {
      this.loadSeriesMetadata(seriesId);
    }

    this.updateLockState();
  };

  initializeContinuousWatching = async () => {
    const { cardData } = this.props;

    if (!cardData || !cardData.seriesMetadata || this.isInitializingContinuousWatching) {
      return;
    }

    this.isInitializingContinuousWatching = true;

    const {
      item,
      seriesMetadata: { id: seriesId, providerInfo },
    } = cardData;

    const continuousWatching = await ContinuousWatching.create(item, providerInfo?.viewingHistoryId ?? seriesId);

    this.setState({ continuousWatching });
    this.isInitializingContinuousWatching = false;
  };

  getProgramMetadata = (): NETGEM_API_V8_METADATA_PROGRAM | null => {
    const { cardData } = this.props;
    const { programMetadata } = this.state;

    return cardData?.programMetadata ?? programMetadata;
  };

  getSeriesMetadata = (): NETGEM_API_V8_METADATA_SERIES | null => {
    const { cardData } = this.props;
    const { seriesMetadata } = this.state;

    return cardData?.seriesMetadata ?? seriesMetadata;
  };

  checkPendingOperation = () => {
    const { cardData, isRegisteredAsGuest } = this.props;

    if (isRegisteredAsGuest || !cardData?.pendingOperation) {
      // User not logged in or no pending operation
      return;
    }

    const {
      pendingOperation: { addToFavoriteData, addToWishlistData, operationType, purchaseData },
    } = cardData;

    /*
     * DEBUG: do not perform pending operation if a subscription was required but nothing has changed
     * // Operation was pending until user logs in
     * if (cardData.pendingOperation?.reason === PendingOperationReason.RequireAccount ||
     * (
     * // Operation was pending until user subscribes
     * cardData.pendingOperation?.reason === PendingOperationReason.RequireSubscription &&
     * Object.values(subscriptions).some((status) => status === SubscriptionStatus.Active || status === SubscriptionStatus.NonRenewing)
     * )) {
     * Messenger.emit(MessengerEvents.OPEN_CARD, cardData);
     * }
     */

    switch (operationType) {
      case PendingOperationKind.AddToFavorite:
        this.performPendingAddToFavorite(addToFavoriteData);
        break;

      case PendingOperationKind.AddToWishlist:
        this.performPendingAddToWishlist(addToWishlistData);
        break;

      case PendingOperationKind.Purchase:
        this.performPendingPurchase(purchaseData);
        break;

      case PendingOperationKind.Play:
        this.performPendingPlay(cardData);
        break;

      case PendingOperationKind.Record:
        // Pending operation is handled at RecordButton level
        break;

      case PendingOperationKind.OpenCard:
        clearPendingOperation();
        break;

      case PendingOperationKind.Subscribe:
        // No effect at card level: if we reach this point, it should be cleared
        clearPendingOperation();
        break;

      // No default
    }
  };

  performPendingAddToFavorite = (addToFavoriteData: ?ADD_TO_FAVORITE_DATA_TYPE) => {
    const { favoriteList, localAddToFavoriteList } = this.props;

    if (!favoriteList) {
      // Favorite list not retrieved yet
      return;
    }

    clearPendingOperation();

    if (!addToFavoriteData || !addToFavoriteData.titId) {
      // Corrupt data: clear pending operation
      return;
    }

    const { titId } = addToFavoriteData;

    if (favoriteList.includes(titId)) {
      // Item is already in favorite list
      return;
    }

    this.toggleFavoriteInternal(titId, localAddToFavoriteList);
  };

  performPendingAddToWishlist = (addToWishlistData: ?ADD_TO_WISHLIST_DATA_TYPE) => {
    const { localAddToWishlist, wishlist, wishlistStatus } = this.props;
    const {
      abortController: { signal },
    } = this;

    if (wishlistStatus !== LoadableStatus.Loaded) {
      // Wishlist not retrieved yet
      return;
    }

    clearPendingOperation();

    if (!addToWishlistData || !addToWishlistData.assetId || !addToWishlistData.channelId) {
      // Corrupt data: clear pending operation
      return;
    }

    const { assetId, channelId } = addToWishlistData;

    if (wishlist.has(assetId)) {
      // Item is already in Wishlist
      return;
    }

    localAddToWishlist(assetId, channelId, signal).catch((error) => ignoreIfAborted(signal, error));
  };

  performPendingPurchase = (purchaseData: ?VOD_PURCHASE_DATA_TYPE): void => {
    const { purchaseList, showVodPurchaseDialog } = this.props;

    if (!purchaseList) {
      // Purchase list not retrieved yet
      return;
    }

    if (!purchaseData?.vodLocationMetadata?.purchaseInfo || !purchaseData?.vodLocationMetadata?.providerInfo?.distributorId) {
      // Corrupt data: clear pending operation
      clearPendingOperation();
      return;
    }

    const {
      otherVtiId,
      vodLocationMetadata: {
        providerInfo: { distributorId },
        purchaseInfo,
      },
    } = purchaseData;

    // User tried to rent or buy while in guest mode: continue purchase
    const { vtiId } = purchaseInfo;
    if (hasBeenPurchased(purchaseList, distributorId, vtiId, otherVtiId)) {
      // Item already purchased
      clearPendingOperation();
    } else {
      Messenger.once(MessengerEvents.MODAL_CLOSED, this.vodPurchaseModalClosedCallback);
      showVodPurchaseDialog(purchaseData);
    }
  };

  performPendingPlay = (cardData: CARD_DATA_MODAL_TYPE) => {
    const {
      parentalControlState: { lockState },
    } = this.state;
    const { distributorId, episodeIndex, item, pendingOperation, programMetadata, seriesMetadata, tileType, urlDefinition, viewingHistoryId, vtiId } = cardData;

    clearPendingOperation();

    if (lockState === ParentalControlLockState.NotInitialized) {
      // Card not ready
      this.uninitializedLockStateTimer = setTimeout(this.performPendingPlay, UNINITIALIZED_LOCK_STATE_DELAY, cardData);
      return;
    }

    if (lockState === ParentalControlLockState.Locked) {
      // Restricted content
      return;
    }

    if (!pendingOperation) {
      // Corrupt data (pending operation has been cleared)
      return;
    }

    if (episodeIndex) {
      // Play specific episode
      this.playSeriesFromEpisode(episodeIndex);
      return;
    }

    const { authority, contentType } = pendingOperation;

    if (!authority || !contentType) {
      // Corrupt data (pending operation has been cleared)
      return;
    }

    this.checkPlay(authority, contentType, item, programMetadata, seriesMetadata, tileType, distributorId ?? null, viewingHistoryId ?? null, vtiId ?? null, urlDefinition);
  };

  startBroadcastStatusRefreshTimer = () => {
    const { broadcastStatus } = this.state;

    this.resetBroadcastStatusRefreshTimer();

    if (broadcastStatus === BroadcastStatus.Past) {
      // A past item will never change, no need to check again
      return;
    }

    this.broadcastStatusRefreshTimer = setTimeout(this.updateBroadcastStatus, REFRESH_TIMEOUT);
  };

  resetBroadcastStatusRefreshTimer = () => {
    if (this.broadcastStatusRefreshTimer) {
      clearTimeout(this.broadcastStatusRefreshTimer);
      this.broadcastStatusRefreshTimer = null;
    }
  };

  resetUninitializedLockStateTimer = () => {
    if (this.uninitializedLockStateTimer) {
      clearTimeout(this.uninitializedLockStateTimer);
      this.uninitializedLockStateTimer = null;
    }
  };

  checkDeeplink = () => {
    const { cardData, deeplink, localGetImageSourceUrl } = this.props;
    const { isSeries } = this.state;

    if (!deeplink || !cardData) {
      return;
    }

    const {
      item: {
        selectedLocation: { duration, previewAvailabilityStartDate },
      },
    } = cardData;
    const programMetadata = this.getProgramMetadata();
    const seriesMetadata = this.getSeriesMetadata();

    if (!programMetadata || !duration || (isSeries && !seriesMetadata)) {
      return;
    }

    const title = getTitle(seriesMetadata ?? programMetadata, Localizer.language);
    const synopsis = getSynopsis(isSeries ? seriesMetadata : programMetadata, Localizer.language);

    if (title && synopsis) {
      const {
        item: { selectedProgramId, seriesId },
      } = cardData;
      const assetId = seriesId ?? selectedProgramId;

      localGetImageSourceUrl(assetId, IMAGE_WIDTH.TileVod, IMAGE_HEIGHT.TileVod)
        .then((imageUrl) => {
          addLinkedData(deeplink, title, synopsis, duration, imageUrl, previewAvailabilityStartDate);
        })
        .catch((error) => {
          logError(`Cannot get image source URL: ${error.message}`);
        });
    }

    // Check if program is blocked by video restrictions
    this.updateLockState();
  };

  updateLockState: () => void = () => {
    const { cardData } = this.props;

    if (!cardData) {
      return;
    }

    const { item } = cardData;
    this.setState({ parentalControlState: ParentalControlManager.getItemStateFromItem(item) });
  };

  updateContentType = () => {
    const { cardData } = this.props;

    if (!cardData) {
      return;
    }

    const { item } = cardData;
    const programMetadata = this.getProgramMetadata();
    const seriesMetadata = this.getSeriesMetadata();
    const { authority } = programMetadata || {};

    const isSeries = typeof item.seriesId === 'string' || seriesMetadata !== null;

    if (!programMetadata || (isSeries && !seriesMetadata)) {
      return;
    }

    this.checkDeeplink();

    this.setState(
      {
        authority,
        contentType: getItemContentType(item, authority),
        imageDisplayType: ImageDisplayType.Unset,
        isSeries,
        purchaseInfo: null,
      },
      () => {
        this.updateCard();
        this.loadItemRelatedData();
        this.loadGameLocationMetadata();
      },
    );
  };

  updateBroadcastStatus = () => {
    const { cardData } = this.props;
    const { contentType } = this.state;

    if (!cardData) {
      return;
    }

    const {
      item,
      item: { type },
    } = cardData;

    // Item is an SVOD but is displayed as a catchup (e.g. Okoo)
    this.setState({ broadcastStatus: contentType === ItemContent.NetgemSVOD && type !== ItemType.Series ? BroadcastStatus.Past : getBroadcastStatus(item) }, this.startBroadcastStatusRefreshTimer);
  };

  updateFollowedPictoClassName = (prevIsFollowed: ?boolean, isFollowed: ?boolean) => {
    let followedPictoClassName = '';

    if (prevIsFollowed === null) {
      // First load of card
      followedPictoClassName = isFollowed ? 'visible' : '';
    } else {
      // Card update
      followedPictoClassName = isFollowed ? 'appearing' : 'disappearing';
    }

    this.setState({ followedPictoClassName });
  };

  updateIsFollowed = () => {
    const { props, state } = this;
    this.setState({ isFollowed: isFavorite(props, state) });
  };

  updateCard = () => {
    const { applicationName, cardData, isNpvrEnabled, npvrRecordingsFuture, npvrRecordingsList, npvrScheduledRecordingsList } = this.props;
    const { authority, broadcastStatus, contentType, isSeries, previewCatchupScheduledEventId } = this.state;

    if (!cardData) {
      return;
    }

    const programMetadata = this.getProgramMetadata();
    const seriesMetadata = this.getSeriesMetadata();
    const {
      item,
      item: {
        locType,
        selectedLocation: { channelId },
      },
    } = cardData;
    const programId = programMetadata?.id ?? '';

    if (isVodItem(item) && authority === NETGEM_API_V8_AUTHENT_REALM_VIDEOFUTUR) {
      this.updateVodStatus();
    }

    updateTitle(applicationName, getTitle(seriesMetadata ?? programMetadata, Localizer.language));

    const { failedRecordingId, hasRecording, hasScheduledRecording, hasSeriesScheduledRecording, recordOutcome, warningScheduledEventId, warningScheduledRecordingId } =
      getEpisodeAndSeriesRecordStatus(programId, seriesMetadata?.id, item, npvrRecordingsList, npvrRecordingsFuture, npvrScheduledRecordingsList, previewCatchupScheduledEventId);

    const scheduledRecordingId = warningScheduledRecordingId || (failedRecordingId ? getScheduledRecordingIdFromRecordingId(failedRecordingId, npvrScheduledRecordingsList) : null);

    const isDeletable =
      isNpvrEnabled && (locType === ItemLocationKind.ScheduledEvent || locType === ItemLocationKind.Recording) && (hasRecording || hasScheduledRecording || hasSeriesScheduledRecording);

    this.recordOutcome = recordOutcome;
    this.failedRecordingId = failedRecordingId;
    this.warningScheduledRecordingId = scheduledRecordingId;
    this.warningScheduledEventId = warningScheduledEventId;

    this.updateChannelImage(channelId);

    this.setState(
      {
        canBePlayed: isItemPlayable(cardData, broadcastStatus, contentType, isSeries, recordOutcome),
        isDeletable,
        isLiveRecording: hasRecording && broadcastStatus === BroadcastStatus.Live,
      },
      this.updateIsFollowed,
    );
  };

  updateChannelImage = (channelId?: string) => {
    const { channels, localGetImageUrl } = this.props;
    const {
      abortController: { signal },
    } = this;

    const channelImageId = getChannelImageId(channels, channelId);

    if (!channelImageId) {
      return;
    }

    localGetImageUrl(channelImageId, IMAGE_WIDTH.Channel, IMAGE_HEIGHT.Channel, Theme.Dark, signal)
      .then((channelImageUrl: string) => {
        signal.throwIfAborted();

        this.setState({ channelImageUrl });
      })
      .catch((error) => ignoreIfAborted(signal, error));
  };

  loadPurchaseInfo = () => {
    const { cardData, localGetPurchaseInfoPerAsset } = this.props;
    const { contentType } = this.state;
    const {
      abortController: { signal },
    } = this;

    if (!cardData) {
      return;
    }

    const {
      item: {
        id,
        purchasable,
        selectedLocation: { channelId, id: selectedLocationId },
      },
    } = cardData;

    if ((contentType !== ItemContent.VODOrDeeplink && contentType !== ItemContent.NetgemSVOD) || !purchasable || getVodKind(selectedLocationId) === VodKind.SVOD) {
      return;
    }

    if (channelId === undefined) {
      logError(`Channel Id is undefined for item ${id}`);
      return;
    }

    localGetPurchaseInfoPerAsset(id, channelId, signal)
      .then((purchaseInfo: NETGEM_API_V8_PURCHASE_INFO) => {
        signal.throwIfAborted();
        this.setState({ purchaseInfo });
      })
      .catch((error) => ignoreIfAborted(signal, error, () => this.setState({ purchaseInfo: null })));
  };

  loadProgramMetadata = (programId: string) => {
    const { localSendV8MetadataRequest } = this.props;
    const {
      abortController: { signal },
    } = this;

    localSendV8MetadataRequest(programId, METADATA_KIND_PROGRAM, signal)
      .then((metadata: NETGEM_API_V8_METADATA) => {
        signal.throwIfAborted();

        const programMetadata = filterCredits(metadata) as Object as NETGEM_API_V8_METADATA_PROGRAM;
        this.setState({ programMetadata }, this.updateContentType);
      })
      .catch((error) => ignoreIfAborted(signal, error));
  };

  loadSeriesMetadata = (seriesId: string) => {
    const { localSendV8MetadataRequest } = this.props;
    const {
      abortController: { signal },
    } = this;

    localSendV8MetadataRequest(seriesId, METADATA_KIND_SERIES, signal)
      .then((metadata: NETGEM_API_V8_METADATA) => {
        signal.throwIfAborted();

        const seriesMetadata = filterCredits(metadata) as Object as NETGEM_API_V8_METADATA_SERIES;
        this.setState({ seriesMetadata }, this.updateContentType);
      })
      .catch((error) => ignoreIfAborted(signal, error, () => this.setState({ seriesMetadata: null })));
  };

  loadItemRelatedData = () => {
    const { cardData } = this.props;
    const { contentType, isSeries } = this.state;

    if (!cardData) {
      return;
    }

    const { item } = cardData;
    const isVod = isVodItem(item);

    if (isVod) {
      this.loadPurchaseInfo();

      if (!isSeries) {
        this.loadVodData();
      }
    } else {
      this.loadTVLocationMetadata(item);
    }

    this.loadLandscapeImage(isVod);
    this.loadStartoverItem(cardData);

    if (contentType === ItemContent.Game) {
      this.setSlideShowImages();
    }
  };

  setSlideShowImages = () => {
    const compareGameImages = (a: NETGEM_API_V8_METADATA_IMAGE, b: NETGEM_API_V8_METADATA_IMAGE) => {
      if (a.tags?.includes('wallpaper')) {
        return -1;
      } else if (b.tags?.includes('wallpaper')) {
        return 1;
      }
      return 0;
    };

    const programMetadata = this.getProgramMetadata();

    if (!programMetadata) {
      return;
    }

    const { images } = programMetadata;
    const slideshowImages = images
      .filter(({ tags }) => tags?.includes('wallpaper') || tags?.includes('screenshot'))
      .sort(compareGameImages)
      .map(({ id }) => id);

    this.setState({ slideshowImages });
  };

  loadTVLocationMetadata = (item: NETGEM_API_V8_FEED_ITEM) => {
    const { cardData, localSendV8MetadataLocationRequest, localSendV8RecordingsMetadataRequest } = this.props;
    const { authority, broadcastStatus } = this.state;
    const {
      abortController: { signal },
    } = this;
    const {
      locType,
      selectedLocation: { id },
    } = item;

    if (!cardData || !id) {
      return;
    }

    const locationStatus = getLocationStatus(item, authority);
    const request =
      locationStatus === WebAppHelpersLocationStatus.Recording || (locationStatus === WebAppHelpersLocationStatus.Live && locType === ItemLocationKind.Recording)
        ? localSendV8RecordingsMetadataRequest
        : localSendV8MetadataLocationRequest;

    request(id, signal)
      .then((requestResponse: NETGEM_API_V8_REQUEST_RESPONSE) => {
        signal.throwIfAborted();

        const {
          result,
          result: {
            location: { target },
          },
        } = requestResponse;
        this.setState(
          {
            previewCatchupScheduledEventId: broadcastStatus === BroadcastStatus.Preview ? target : null,
            tvLocationMetadata: result,
          },
          this.checkPendingOperation,
        );
      })
      .catch((error) => ignoreIfAborted(signal, error));
  };

  handleInfoOnClick = (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => {
    const { isDebugModeEnabled } = this.props;

    if (isDebugModeEnabled && event.ctrlKey) {
      event.preventDefault();
      event.stopPropagation();

      const { props, state } = this;
      showDebug('Card', {
        instance: this,
        instanceFields: ['failedRecordingId', 'recordOutcome', 'warningScheduledEventId', 'warningScheduledRecordingId'],
        props,
        propsFields: ['cardData'],
        state,
        stateFields: [
          'authority',
          'backgroundImageUrl',
          'broadcastStatus',
          'canBePlayed',
          'channelImageUrl',
          'contentType',
          'continuousWatching',
          'followedPictoClassName',
          'gameLocationMetadata',
          'imageDisplayType',
          'isDeletable',
          'isFollowed',
          'isLiveRecording',
          'isSeries',
          'isWaitingForDeleteConfirmation',
          'isWaitingForRecordConfirmation',
          'parentalControlState',
          'portraitImageUrl',
          'previewCatchupScheduledEventId',
          'programMetadata',
          'purchaseInfo',
          'seriesMetadata',
          'startoverItem',
          'tvLocationMetadata',
          'vodLocationsMetadata',
          'vodStatus',
        ],
      });
    }
  };

  loadLandscapeImage = (isVod: boolean) => {
    const { cardData } = this.props;
    const {
      abortController: { signal },
    } = this;

    if (!cardData) {
      return;
    }

    let imageId: string | null = null;

    if (isVod && !parseBoolean(process.env.REACT_APP_FORCE_LANDSCAPE_VOD_CARD)) {
      const {
        item: {
          locType,
          selectedLocation: { channelId },
          seriesId,
        },
      } = cardData;
      const programMetadata = this.getProgramMetadata();
      const seriesMetadata = this.getSeriesMetadata();
      const imagesMetadata = typeof seriesId === 'string' ? seriesMetadata?.images : programMetadata?.images;

      if (imagesMetadata) {
        imageId = findLandscapeImageId(imagesMetadata, IMAGE_TAG_NO_TEXT);
      }

      // Special OCS case: images are in landscape but program metadata does not contain their list
      const isPortraitAllowed = locType !== ItemLocationKind.SVOD || !channelId || !/canalplusocs/iu.test(channelId);

      if (!imageId && isPortraitAllowed) {
        // If landscape image without text is not found, let's load portrait image
        this.loadPortraitImage();
        return;
      }
    }

    this.loadImage(IMAGE_WIDTH.CardBackground, IMAGE_HEIGHT.CardBackground, signal, imageId)
      .then((imageUrl) => {
        // Image is set to background image whatever its orientation because if it's portrait, it will be blurred
        this.setState({
          backgroundImageUrl: imageUrl,
          imageDisplayType: ImageDisplayType.Landscape,
        });
      })
      .catch(() => {
        if (signal.aborted) {
          return;
        }
        this.setState({ backgroundImageUrl: null });
      });
  };

  loadPortraitImage = () => {
    const {
      abortController: { signal },
    } = this;

    this.loadImage(IMAGE_WIDTH.TileVod, IMAGE_HEIGHT.TileVod, signal)
      .then((portraitImageUrl) =>
        this.setState({
          backgroundImageUrl: portraitImageUrl,
          imageDisplayType: ImageDisplayType.Portrait,
          portraitImageUrl,
        }),
      )
      .catch(() => {
        if (signal.aborted) {
          return;
        }
        this.setState({ portraitImageUrl: null });
      });
  };

  loadImage = (width: number, height: number, signal: AbortSignal, imageId: ?string): Promise<string> => {
    const { cardData, localGetImageUrl } = this.props;

    if (!cardData) {
      return Promise.reject(new Error('No cardData'));
    }

    const {
      item: { selectedProgramId, seriesId },
    } = cardData;
    const assetId = seriesId ?? selectedProgramId;

    return localGetImageUrl(imageId ?? assetId, width, height, undefined, signal).then((response) => {
      signal.throwIfAborted();

      return response;
    });
  };

  loadStartoverItem = (cardData: CARD_DATA_MODAL_TYPE) => {
    const { channels, localSendV8LocationCatchupForAssetRequest } = this.props;
    const {
      abortController: { signal },
    } = this;
    const {
      item,
      item: { locType, selectedLocation, selectedProgramId },
    } = cardData;

    if (!selectedLocation) {
      this.setState({ startoverItem: null });
      return;
    }

    const { channelId } = selectedLocation;

    if (!channelHasStartover(channels, channelId) || locType !== ItemLocationKind.ScheduledEvent || !isItemLiveOrAboutToStart(item)) {
      this.setState({ startoverItem: null });
      return;
    }

    localSendV8LocationCatchupForAssetRequest(selectedProgramId, AccurateTimestamp.now(), MILLISECONDS_PER_HOUR, signal)
      .then((requestResponse: NETGEM_API_V8_REQUEST_RESPONSE) => {
        signal.throwIfAborted();

        const startoverItem: NETGEM_API_V8_FEED_ITEM | null = getStartOverItem(item, requestResponse);
        this.setState({ startoverItem });
      })
      .catch((error) => ignoreIfAborted(signal, error, () => this.setState({ startoverItem: null })));
  };

  loadVodData = () => {
    const { cardData, localGetVodLocations } = this.props;
    const { isSeries } = this.state;
    const {
      abortController: { signal },
    } = this;

    if (!cardData) {
      return;
    }

    const {
      item,
      item: { elements, locations },
    } = cardData;

    if (!isVodItem(item)) {
      return;
    }

    if (!locations) {
      if (isSeries && elements) {
        fireAndForget(this.loadEpisodesVodData(elements), 'CardModal.loadEpisodesVodData');
      }
      return;
    }

    // VOD program
    localGetVodLocations(locations, signal)
      .then((vodLocationsMetadata) => {
        signal.throwIfAborted();

        this.setState({ vodLocationsMetadata }, () => {
          this.updateIsFollowed();
          this.checkPendingOperation();
        });
      })
      .catch((error) => ignoreIfAborted(signal, error));
  };

  loadEpisodesVodData = async (elements: NETGEM_API_V8_RAW_FEED) => {
    const { localGetVodLocations } = this.props;
    const {
      abortController: { signal },
    } = this;

    // Extract locations
    const locationsList: Array<Array<NETGEM_API_V8_ITEM_LOCATION>> = [];
    for (const { locations } of elements) {
      if (locations) {
        locationsList.push(locations);
      }
    }

    const vodLocationsMetadata = await loadEpisodesVodLocationMetadata(locationsList, localGetVodLocations, signal);

    if (vodLocationsMetadata === null) {
      // Error case
      return;
    }

    this.setState({ vodLocationsMetadata }, this.checkPendingOperation);
  };

  loadGameLocationMetadata = () => {
    const { cardData, localSendV8MetadataLocationRequest } = this.props;
    const { contentType } = this.state;
    const {
      abortController: { signal },
    } = this;

    if (!cardData) {
      return;
    }

    const {
      item: {
        selectedLocation: { id },
      },
    } = cardData;

    if (!id || contentType !== ItemContent.Game) {
      return;
    }

    localSendV8MetadataLocationRequest(id, signal)
      .then((requestResponse: NETGEM_API_V8_REQUEST_RESPONSE) => {
        signal.throwIfAborted();

        const { result } = requestResponse;
        this.setState({ gameLocationMetadata: result });
      })
      .catch((error) => ignoreIfAborted(signal, error));
  };

  updateVodStatus = () => {
    const { cardData, purchaseList } = this.props;
    const { isSeries, vodLocationsMetadata } = this.state;

    if (!cardData) {
      return;
    }

    const {
      isOpenFromPlayer,
      item,
      item: { locType },
      locationType,
    } = cardData;
    if (!isVodItem(item)) {
      return;
    }

    const localLocType = locType ?? locationType;

    if (!isSeries) {
      this.setState({ vodStatus: getVodStatusFromLocations(vodLocationsMetadata, purchaseList) });
    } else if (localLocType === ItemLocationKind.SVOD) {
      // SVOD series: display message at series level
      this.setState({ vodStatus: { status: BOVodAssetStatus.Free } });
    } else if (isOpenFromPlayer) {
      // TVOD or EST: episode open from player --> obviously rented or bought
      this.setState({ vodStatus: { status: localLocType === ItemLocationKind.EST ? BOVodAssetStatus.Bought : BOVodAssetStatus.Rented } });
    }
  };

  playSeries = async (): Promise<void> => {
    const { cardData } = this.props;
    const { authority, contentType, continuousWatching } = this.state;

    if (!cardData) {
      logWarning('Missing cardData');
      return;
    }

    if (!continuousWatching) {
      logWarning('Missing continuousWatching');
      return;
    }

    const resumeData = await continuousWatching.getData();

    if (resumeData === null) {
      logWarning('Cannot build resume data');
      return;
    }

    const { distributorId, item, locationMetadata, programMetadata, viewingHistoryId, vtiId } = resumeData;

    const { tileType, urlDefinition } = cardData;
    const seriesMetadata = this.getSeriesMetadata();

    this.checkPlay(authority, contentType, item, programMetadata, seriesMetadata, tileType, distributorId, viewingHistoryId ?? null, vtiId, urlDefinition, locationMetadata, continuousWatching);
  };

  playSeriesFromEpisode = (index: number) => {
    const { cardData, isRegisteredAsGuest, localHideModal } = this.props;
    const { purchaseInfo, vodLocationsMetadata, vodStatus } = this.state;

    if (isRegisteredAsGuest) {
      // Guest mode: let's open the sign-in page
      if (!cardData) {
        return;
      }

      const { item, tileType, urlDefinition } = cardData;
      const programMetadata = this.getProgramMetadata();

      const cardDataLocal: CARD_DATA_MODAL_TYPE = {
        distributorId: getDistributorId(vodLocationsMetadata, purchaseInfo, programMetadata),
        episodeIndex: index,
        item,
        pendingOperation: {
          operationType: PendingOperationKind.Play,
          reason: PendingOperationReason.RequireAccount,
        },
        programMetadata,
        seriesMetadata: this.getSeriesMetadata(),
        tileType,
        urlDefinition,
        viewingHistoryId: vodStatus?.viewingHistoryId ?? null,
        vtiId: vodStatus?.vtiId ?? null,
      };

      storePendingOperation(cardDataLocal);
      localHideModal();
      Messenger.emit(MessengerEvents.SHOW_SIGN_IN, PendingOperationKind.Play);
      return;
    }

    this.setState(
      produce((draft) => {
        draft.continuousWatching?.changeResumeEpisode(index);
      }),
      this.playSeries,
    );
  };

  handlePlayGameButtonOnClick = (): Promise<void> => {
    const {
      appConfiguration,
      cardData,
      localGetGamestreamToken,
      localHideModal,
      localReopenModalCard,
      localShowConfirmation,
      localShowEula,
      localShowGameProfileSelect,
      localShowMessage,
      localUpdatePlayingHistory,
      playingHistory,
      playingHistoryStatus,
    } = this.props;
    const { gameLocationMetadata } = this.state;

    if (!cardData) {
      return Promise.resolve();
    }

    const {
      item,
      item: { selectedProgramId },
    } = cardData;

    if (gameLocationMetadata === null) {
      logError('Cannot launch game with gameLocationMetadata = null');
      return Promise.resolve();
    }

    const programMetadata = this.getProgramMetadata();
    const isGamepadOnly = programMetadata?.gameMetadata?.isMouseAndKeyboard === false;

    const gameAlias = getGameAlias(gameLocationMetadata);
    const profileId = searchMostRecentProfileForGame(playingHistoryStatus, playingHistory, selectedProgramId);
    const avatarBaseUrl = getSettingValueByName('account.service.gamestream', 'avatarsurl', appConfiguration) ?? '';

    if (gameAlias === null) {
      logError('Game alias missing in metadata');
      return Promise.resolve();
    }

    localHideModal(true);

    // If profileId is null, the first profile of the list will be used
    return GameManager.launchGame({
      avatarBaseUrl,
      gameAlias,
      getGamestreamToken: localGetGamestreamToken,
      isGamepadOnly,
      item,
      playingHistory: playingHistoryArrayToObject(playingHistory),
      playingHistoryStatus,
      profileId,
      promptSessionClose: localShowConfirmation,
      reopenModalCard: localReopenModalCard,
      showEula: localShowEula,
      showGameProfileSelect: localShowGameProfileSelect,
      showMessage: localShowMessage,
      updatePlayingHistoryItem: localUpdatePlayingHistory,
    });
  };

  handlePlaySeriesButtonOnClick = (): Promise<void> => this.playSeries();

  handlePlayButtonOnClick = () => {
    const { cardData } = this.props;
    const { authority, contentType, purchaseInfo, vodLocationsMetadata, vodStatus } = this.state;

    if (!cardData) {
      return;
    }

    const { item, tileType, urlDefinition } = cardData;
    const programMetadata = this.getProgramMetadata();
    const seriesMetadata = this.getSeriesMetadata();
    const distributorId = getDistributorId(vodLocationsMetadata, purchaseInfo, programMetadata);

    this.checkPlay(authority, contentType, item, programMetadata, seriesMetadata, tileType, distributorId, vodStatus?.viewingHistoryId ?? null, vodStatus?.vtiId ?? null, urlDefinition);
  };

  checkPlay = (
    authority?: NETGEM_API_V8_AUTHENT_REALM,
    contentType: ItemContent,
    item: NETGEM_API_V8_FEED_ITEM,
    programMetadata: NETGEM_API_V8_METADATA_PROGRAM | null,
    seriesMetadata: NETGEM_API_V8_METADATA_SERIES | null,
    tileType: TileConfigTileType,
    distributorId: string | null,
    viewingHistoryId: string | null,
    vtiId: number | null,
    urlDefinition?: NETGEM_API_V8_URL_DEFINITION | null,
    locationMetadata?: NETGEM_API_V8_METADATA_SCHEDULE,
    continuousWatching?: ContinuousWatching,
  ) => {
    const { channels, commercialOffers, defaultRights, isRegisteredAsGuest, isSubscriptionFeatureEnabled, localHideModal, userRights } = this.props;
    const { previewCatchupScheduledEventId, tvLocationMetadata, vodLocationsMetadata } = this.state;

    const localLocationMetadata = locationMetadata ?? tvLocationMetadata ?? getVodLocationFromVtiId(vodLocationsMetadata, vtiId);

    const cardDataLocal: CARD_DATA_MODAL_TYPE = {
      distributorId,
      item,
      programMetadata,
      seriesMetadata,
      tileType,
      urlDefinition,
      viewingHistoryId,
      vtiId,
    };

    if (
      !isPlaybackGranted(
        isRegisteredAsGuest,
        isSubscriptionFeatureEnabled,
        cardDataLocal,
        authority,
        contentType,
        localLocationMetadata?.location,
        channels,
        commercialOffers,
        defaultRights,
        userRights,
      )
    ) {
      // Playback denied: log-in or subscribe page has been requested
      localHideModal();
      return;
    }

    if (contentType === ItemContent.VODOrDeeplink) {
      if (vtiId) {
        Messenger.emit(MessengerEvents.OPEN_PLAYER, {
          continuousWatching,
          distributorId,
          item,
          locationId: localLocationMetadata?.location.id,
          programMetadata,
          seriesMetadata,
          tileType,
          type: ExtendedItemType.VOD,
          viewingHistoryId,
          vtiId,
        });
      }
    } else {
      Messenger.emit(MessengerEvents.OPEN_PLAYER, {
        authority,
        continuousWatching,
        item,
        previewCatchupScheduledEventId,
        programMetadata,
        seriesMetadata,
        type: ExtendedItemType.TV,
      });
    }
  };

  handleTrailerButtonOnClick = () => {
    const { cardData, localShowMessage, localShowPinModal } = this.props;
    const {
      parentalControlState: { hasPinCode, lockReason, lockState },
    } = this.state;

    if (!cardData) {
      return;
    }

    if (!hasPinCode && lockReason === ParentalControlLockReason.AdultContent) {
      // Content is locked and cannot be unlocked
      showUnavailableContentModal(localShowMessage);
      return;
    }

    const playTrailer = (isValidPin: boolean): void => {
      if (!isValidPin) {
        return;
      }

      Messenger.emit(MessengerEvents.OPEN_PLAYER, {
        programMetadata: this.getProgramMetadata(),
        seriesMetadata: this.getSeriesMetadata(),
        type: ExtendedItemType.Trailer,
      });
    };

    if (lockState !== ParentalControlLockState.Unlocked) {
      // Item locked or not initialized yet
      if (hasPinCode) {
        // Ask PIN code
        Messenger.once(MessengerEvents.MODAL_CLOSED, playTrailer);
        localShowPinModal({ item: cardData?.item, kind: lockReason === ParentalControlLockReason.AdultContent ? PinModalKind.UnlockAdultContent : PinModalKind.UnlockContent });
      } else {
        // PIN code creation
        localShowPinModal({ kind: PinModalKind.CreateMaster });
      }
      return;
    }

    // Regular case: item can be rented or bought
    playTrailer(true);
  };

  handleAddToWishlistButtonOnClick = (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const { cardData, localAddToFavoriteList, localAddToWishlist } = this.props;
    const { authority } = this.state;

    if (!cardData) {
      return;
    }

    if (authority === NETGEM_API_V8_AUTHENT_REALM_VIDEOFUTUR) {
      this.toggleFavorite(cardData, localAddToFavoriteList);
    } else {
      this.toggleWishlist(cardData, localAddToWishlist);
    }
  };

  handleRemoveFromWishlistButtonOnClick = (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const { cardData, localRemoveFromFavoriteList, localRemoveFromWishlist } = this.props;
    const { authority } = this.state;

    if (!cardData) {
      return;
    }

    if (authority === NETGEM_API_V8_AUTHENT_REALM_VIDEOFUTUR) {
      this.toggleFavorite(cardData, localRemoveFromFavoriteList);
    } else {
      this.toggleWishlist(cardData, localRemoveFromWishlist);
    }
  };

  toggleWishlist = (cardData: CARD_DATA_MODAL_TYPE, toggler: (assetId: string, channelId: string, signal?: AbortSignal) => Promise<void>) => {
    const { channels, commercialOffers, defaultRights, isRegisteredAsGuest, isSubscriptionFeatureEnabled, localHideModal, userRights } = this.props;
    const { tvLocationMetadata, vodLocationsMetadata, vodStatus } = this.state;
    const {
      abortController: { signal },
    } = this;

    const programMetadata = this.getProgramMetadata();
    const seriesMetadata = this.getSeriesMetadata();

    const {
      item: {
        selectedLocation: { channelId },
        selectedProgramId,
      },
    } = cardData;
    const assetId = seriesMetadata?.id ?? selectedProgramId;

    if (!channelId) {
      // Channel Id is null
      return;
    }

    const locationMetadata = tvLocationMetadata ?? getVodLocationFromVtiId(vodLocationsMetadata, vodStatus?.vtiId);

    const cardDataLocal = {
      ...cardData,
      programMetadata,
      seriesMetadata,
    };

    if (!isWishlistGranted(isRegisteredAsGuest, isSubscriptionFeatureEnabled, cardDataLocal, locationMetadata?.location, channels, commercialOffers, defaultRights, userRights)) {
      // Add to wishlist denied: log-in or subscribe page has been requested
      localHideModal();
      return;
    }

    toggler(assetId, channelId, signal).catch((error) => ignoreIfAborted(signal, error));
  };

  toggleFavoriteInternal = (titId: string, toggler: (titId: string, signal?: AbortSignal) => Promise<void>) => {
    const {
      abortController: { signal },
    } = this;

    toggler(titId, signal).catch((error) => ignoreIfAborted(signal, error));
  };

  toggleFavorite = (cardData: CARD_DATA_MODAL_TYPE, toggler: (titId: string, signal?: AbortSignal) => Promise<void>) => {
    const { channels, commercialOffers, defaultRights, isRegisteredAsGuest, isSubscriptionFeatureEnabled, localHideModal, userRights } = this.props;
    const { vodLocationsMetadata, vodStatus } = this.state;

    const programMetadata = this.getProgramMetadata();
    const seriesMetadata = this.getSeriesMetadata();

    if (!programMetadata) {
      return;
    }

    const { providerInfo } = programMetadata;

    if (!providerInfo) {
      return;
    }

    const locationMetadata = getVodLocationFromVtiId(vodLocationsMetadata, vodStatus?.vtiId);
    const cardDataLocal: CARD_DATA_MODAL_TYPE = {
      ...cardData,
      programMetadata,
      seriesMetadata,
    };

    const titId = getTitIdFromProviderInfo(providerInfo, seriesMetadata);

    if (!isFavoriteGranted(isRegisteredAsGuest, isSubscriptionFeatureEnabled, cardDataLocal, titId, locationMetadata?.location, channels, commercialOffers, defaultRights, userRights)) {
      // Add to favorite list denied: log-in or subscribe page has been requested
      localHideModal();
      return;
    }

    this.toggleFavoriteInternal(titId, toggler);
  };

  vodPurchaseModalClosedCallback = (data?: CloseCallbackType): void => {
    const { cardData } = this.props;
    const { purchaseInfo, vodLocationsMetadata } = this.state;

    if (!data) {
      // Purchase canceled
      return;
    }

    const { locationId, playNow, viewingHistoryId, vtiId } = data;

    if (cardData === undefined || !playNow || vtiId === undefined) {
      return;
    }

    const programMetadata = this.getProgramMetadata();
    const seriesMetadata = this.getSeriesMetadata();

    const { item } = cardData;

    Messenger.emit(MessengerEvents.OPEN_PLAYER, {
      distributorId: getDistributorId(vodLocationsMetadata, purchaseInfo, programMetadata),
      item,
      locationId,
      programMetadata,
      seriesMetadata,
      type: ExtendedItemType.VOD,
      viewingHistoryId,
      vtiId,
    });
  };

  handlePurchaseButtonOnClick = (data: BASE_VOD_PURCHASE_DATA_TYPE): void => {
    const { cardData, isRegisteredAsGuest, localHideModal, localShowMessage, localShowPinModal, showVodPurchaseDialog } = this.props;
    const {
      parentalControlState: { hasPinCode, lockReason, lockState },
    } = this.state;

    if (!cardData) {
      return;
    }

    const programMetadata = this.getProgramMetadata();
    const seriesMetadata = this.getSeriesMetadata();

    if (!programMetadata) {
      return;
    }

    if (!hasPinCode && lockReason === ParentalControlLockReason.AdultContent) {
      // Content is locked and cannot be unlocked
      showUnavailableContentModal(localShowMessage);
      return;
    }

    const purchaseInternal = (isValidPin: boolean): void => {
      if (!isValidPin) {
        return;
      }

      const { definition, displayPrice, distributorId, itemCount, licenseDuration, locationId, otherVtiId, purchaseType, vodLocationMetadata, vtiId } = data;

      const providerInfo = vodLocationMetadata?.providerInfo ?? programMetadata.providerInfo;
      const { item } = cardData;

      const purchaseData: VOD_PURCHASE_DATA_TYPE = {
        definition,
        displayPrice,
        distributorId,
        item,
        itemCount,
        licenseDuration,
        locationId,
        otherVtiId,
        programMetadata,
        purchaseType,
        seriesMetadata,
        viewingHistoryId: providerInfo?.viewingHistoryId,
        vodLocationMetadata,
        vtiId,
      };

      if (isRegisteredAsGuest) {
        // Guest mode: let's open the sign-in page
        storePendingOperation({
          ...cardData,
          pendingOperation: {
            operationType: PendingOperationKind.Purchase,
            purchaseData,
            reason: PendingOperationReason.RequireAccount,
          },
        });
        localHideModal();
        Messenger.emit(MessengerEvents.SHOW_SIGN_IN, PendingOperationKind.Purchase);
      } else {
        Messenger.once(MessengerEvents.MODAL_CLOSED, this.vodPurchaseModalClosedCallback);
        showVodPurchaseDialog(purchaseData);
      }
    };

    if (lockState !== ParentalControlLockState.Unlocked) {
      // Item locked or not initialized yet
      if (hasPinCode) {
        // Ask PIN code
        Messenger.once(MessengerEvents.MODAL_CLOSED, purchaseInternal);
        localShowPinModal({ item: cardData?.item, kind: lockReason === ParentalControlLockReason.AdultContent ? PinModalKind.UnlockAdultContent : PinModalKind.UnlockContent });
      } else {
        // PIN code creation
        localShowPinModal({ kind: PinModalKind.CreateMaster });
      }
      return;
    }

    // Regular case: item can be rented or bought
    purchaseInternal(true);
  };

  openPreviousCard = () => {
    const { cardData } = this.props;

    if (!cardData || !cardData.previousCard) {
      return;
    }

    const { previousCard } = cardData;
    Messenger.emit(MessengerEvents.OPEN_CARD, previousCard);
  };

  renderChannelImage = (): React.Node => {
    const { cardData, channels } = this.props;
    const { channelImageUrl } = this.state;

    if (!channelImageUrl) {
      return null;
    }

    const channelName = getChannelName(channels, cardData?.item.selectedLocation.channelId);

    return <img alt={Localizer.localize('accessibility.navigation.channel_logo', { channelName })} className='channelImage' src={channelImageUrl} />;
  };

  renderTitleAndSynopsis = (cardData: CARD_DATA_MODAL_TYPE, title: string | null): { synopsisElement: React.Node, titleElement: React.Node } => {
    const { isSeries } = this.state;

    const { isOpenFromPlayer } = cardData;
    const programMetadata = this.getProgramMetadata();
    const seriesMetadata = this.getSeriesMetadata();

    const synopsisElement = (
      <div className='synopsis'>
        <span className='screenReaderOnly'>{Localizer.localize('accessibility.content.synopsis')}</span>
        {getSynopsis(isSeries && !isOpenFromPlayer ? seriesMetadata : programMetadata, Localizer.language)}
      </div>
    );

    const seriesTitle = getTitle(seriesMetadata, Localizer.language);
    const episodeNumber = programMetadata?.episodeOf?.episodeNumber;
    const episodeNumberElt = episodeNumber ? `${episodeNumber} - ` : '';

    // Attribute "key" is only there to trigger a re-render when the title change, and so to trigger the animation
    const titleElement = (
      <div className='text'>
        <div className='title' id='modal-title' key={title}>
          {seriesTitle ?? title}
        </div>
        {isOpenFromPlayer && seriesTitle && seriesTitle !== title ? (
          <div className='subtitle'>
            {episodeNumberElt}
            {title}
          </div>
        ) : null}
      </div>
    );

    return {
      synopsisElement,
      titleElement,
    };
  };

  renderBroadcastTime = (startTime: number, endTime: number): React.Node => {
    const { cardData } = this.props;
    const { contentType, isSeries } = this.state;

    if (!cardData) {
      return null;
    }

    const {
      item: { locType, selectedLocation },
    } = cardData;
    const { availabilityEndDate } = selectedLocation || {};
    const details = [];

    if ((locType === ItemLocationKind.Catchup || locType === ItemLocationKind.Recording) && availabilityEndDate) {
      details.push(
        <div className='date expiration' key='expirationDate'>
          {formatExpirationDate(availabilityEndDate)}
        </div>,
      );
    }

    if (!isSeries && contentType === ItemContent.TV) {
      details.push(
        <div className='date broadcast' key='broadcastDate'>
          {formatDate(startTime, endTime, AccurateTimestamp.nowInSeconds())}
        </div>,
      );
    }

    return <>{details.map((d) => d)}</>;
  };

  renderProgramOrSeriesInfo = (isVodOrDeeplink: boolean): Array<React.Node> | null => {
    const { cardData } = this.props;
    const { isSeries, vodLocationsMetadata } = this.state;

    if (!cardData) {
      return null;
    }

    const { item } = cardData;
    const seriesMetadata = this.getSeriesMetadata();
    const metadata = isSeries && seriesMetadata ? seriesMetadata : this.getProgramMetadata();

    let programInfo = PROGRAM_INFO.ParentalGuidance | PROGRAM_INFO.ReleaseDate | PROGRAM_INFO.Language | PROGRAM_INFO.Genre | PROGRAM_INFO.ProductionCountry;
    if (!isSeries && (!isVodOrDeeplink || !seriesMetadata)) {
      // Duration should also be displayed
      programInfo |= PROGRAM_INFO.Duration;
    }

    return renderProgramDetails(item, metadata, vodLocationsMetadata, programInfo);
  };

  renderGameInfo = (): React.Node => {
    const { cardData } = this.props;

    if (cardData?.item.locType !== ItemLocationKind.Game) {
      return null;
    }

    const programMetadata = this.getProgramMetadata();

    if (programMetadata?.gameMetadata === undefined) {
      return null;
    }

    const {
      gameMetadata: { developers, publishers },
    } = programMetadata;

    return (
      <>
        <div className='gameInfo'>
          <span>{Localizer.localize('gaming.published_by')}</span> <span className='gameInfoValue'>{publishers.join(', ')}</span>
        </div>
        <div className='gameInfo'>
          <span>{Localizer.localize('gaming.developed_by')}</span> <span className='gameInfoValue'>{developers.join(', ')}</span>
        </div>
      </>
    );
  };

  renderGameCopyright = (): React.Node => {
    const { cardData } = this.props;

    if (cardData?.item.locType !== ItemLocationKind.Game) {
      return null;
    }

    const programMetadata = this.getProgramMetadata();

    if (programMetadata?.copyright === undefined) {
      return null;
    }

    const { copyright } = programMetadata;
    return <div className='gameCopyright'>{copyright}</div>;
  };

  renderContinuousWatchingButton = (): React.Node => {
    const { cardData, isRegisteredAsGuest, purchaseList } = this.props;
    const { continuousWatching, purchaseInfo } = this.state;

    if (isRegisteredAsGuest || continuousWatching === null || !cardData) {
      return null;
    }

    const {
      isOpenFromPlayer,
      item: { locType },
    } = cardData;

    if (isOpenFromPlayer) {
      // When opening episode card from player, no "play" button is displayed
      return null;
    }

    if (locType && ![ItemLocationKind.Catchup, ItemLocationKind.Recording, ItemLocationKind.SVOD].includes(locType) && (purchaseInfo === null || !isPackPurchased(purchaseInfo, purchaseList))) {
      // Series not purchased or wrong type of content
      return null;
    }

    // Season item has been purchased: display "Watch"/"Resume" button
    return (
      <ButtonFX key='playSeries' onClick={this.handlePlaySeriesButtonOnClick}>
        {continuousWatching.buttonText}
      </ButtonFX>
    );
  };

  renderVodActionPanel = (): React.Node => {
    const { cardData, streamPriorities } = this.props;
    const { isSeries, purchaseInfo, vodLocationsMetadata, vodStatus } = this.state;

    if (!cardData) {
      return null;
    }

    const {
      isOpenFromPlayer,
      item: { locType },
    } = cardData;

    const programMetadata = this.getProgramMetadata();
    const seriesMetadata = this.getSeriesMetadata();

    const status = vodStatus?.status ?? BOVodAssetStatus.Unknown;
    const expirationTime = vodStatus?.expirationTime ?? 0;

    const isPlayButtonDisplayed =
      !isOpenFromPlayer && !isSeries && (status === BOVodAssetStatus.Free || status === BOVodAssetStatus.Rented || status === BOVodAssetStatus.Bought || locType === (VodKind.SVOD as string));

    const progress = getWatchingStatus(this.props, this.state);

    return (
      <>
        <PricingVod
          context={PricingContext.Card}
          expirationTime={expirationTime}
          isVodSeries={isSeries}
          locationsMetadata={vodLocationsMetadata}
          onClick={this.handlePurchaseButtonOnClick}
          purchaseInfo={purchaseInfo}
          status={status}
        />
        {this.renderContinuousWatchingButton()}
        {isPlayButtonDisplayed ? (
          <ButtonFX allowZeroProgress={false} autoFocus key='play' onClick={this.handlePlayButtonOnClick} progress={progress}>
            {Localizer.localize(progress !== null ? 'common.actions.resume' : 'common.actions.watch')}
          </ButtonFX>
        ) : null}
        {!isOpenFromPlayer && hasTrailer(streamPriorities, programMetadata, seriesMetadata) ? (
          <ButtonFX key='trailer' onClick={this.handleTrailerButtonOnClick}>
            {Localizer.localize('modal.card.actions.trailer')}
          </ButtonFX>
        ) : null}
        {this.renderWishlistButton()}
      </>
    );
  };

  renderGameActionPanel = (): React.Node => {
    const { streamPriorities } = this.props;
    const { gameLocationMetadata } = this.state;

    if (gameLocationMetadata === null) {
      // Metadata not loaded yet
      return null;
    }

    const programMetadata = this.getProgramMetadata();
    const seriesMetadata = this.getSeriesMetadata();

    return (
      <>
        <ButtonFX key='play' onClick={this.handlePlayGameButtonOnClick}>
          {Localizer.localize('gaming.actions.play')}
        </ButtonFX>
        {hasTrailer(streamPriorities, programMetadata, seriesMetadata) ? (
          <ButtonFX key='trailer' onClick={this.handleTrailerButtonOnClick}>
            {Localizer.localize('modal.card.actions.trailer')}
          </ButtonFX>
        ) : null}
        {this.renderWishlistButton()}
      </>
    );
  };

  renderWishlistButton = (): React.Node => {
    const { followedPictoClassName, isFollowed } = this.state;

    const PictoWishlistFull = process.env.REACT_APP_ID === APPLICATION_ID.FranceChannel ? PictoBookmark : PictoHeartFull;
    const PictoWishlistEmpty = process.env.REACT_APP_ID === APPLICATION_ID.FranceChannel ? PictoBookmarkEmpty : PictoHeartEmpty;

    if (isFollowed) {
      return (
        <ButtonFX className='favoriteButton' key='follow' onClick={this.handleRemoveFromWishlistButtonOnClick}>
          <PictoWishlistFull className={clsx('favorite', followedPictoClassName, process.env.REACT_APP_ID === APPLICATION_ID.FranceChannel && 'franceChannel')} />
          <span>{Localizer.localize('modal.card.actions.remove_from_favorites')}</span>
        </ButtonFX>
      );
    }

    return (
      <ButtonFX className='favoriteButton' key='follow' onClick={this.handleAddToWishlistButtonOnClick}>
        <PictoWishlistEmpty aria-hidden className={clsx('favorite', followedPictoClassName, process.env.REACT_APP_ID === APPLICATION_ID.FranceChannel && 'franceChannel')} />
        <span>{Localizer.localize('modal.card.actions.add_to_favorites')}</span>
      </ButtonFX>
    );
  };

  renderTVActionPanel = (): React.Node => {
    const { cardData, streamPriorities } = this.props;
    const { broadcastStatus, canBePlayed, isSeries, previewCatchupScheduledEventId, tvLocationMetadata } = this.state;

    if (!cardData) {
      return null;
    }

    const programMetadata = this.getProgramMetadata();
    const seriesMetadata = this.getSeriesMetadata();

    const {
      isOpenFromPlayer,
      isTimeshifting,
      item,
      item: { locType },
      tileType,
      urlDefinition,
    } = cardData;
    const progress = broadcastStatus === BroadcastStatus.Live ? getLiveProgress(cardData) : getWatchingStatus(this.props, this.state);

    // Recording a program is not allowed while timeshifting
    const isRecordingAllowed = (locType === ItemLocationKind.ScheduledEvent || locType === ItemLocationKind.Catchup || locType === ItemLocationKind.Recording) && (isSeries || isTimeshifting !== true);

    // Deduce record king from location type, then from card type (series Vs. single)
    let recordKind: Undefined<ScheduledRecordingsKindType> = undefined;
    if (isSeries) {
      recordKind = ScheduledRecordingsKind.Series;
    } else if (locType === ItemLocationKind.Catchup) {
      recordKind = ScheduledRecordingsKind.KeepFromReplay;
    }

    return (
      <>
        {this.renderContinuousWatchingButton()}
        {canBePlayed ? (
          <ButtonFX allowZeroProgress={broadcastStatus === BroadcastStatus.Live} autoFocus key='play' onClick={this.handlePlayButtonOnClick} progress={progress}>
            {Localizer.localize(
              broadcastStatus === BroadcastStatus.Live && locType !== ItemLocationKind.Recording ? 'common.actions.watch_live' : progress !== null ? 'common.actions.resume' : 'common.actions.watch',
            )}
          </ButtonFX>
        ) : null}
        {!isOpenFromPlayer && hasTrailer(streamPriorities, programMetadata, seriesMetadata) ? (
          <ButtonFX key='trailer' onClick={this.handleTrailerButtonOnClick}>
            {Localizer.localize('modal.card.actions.trailer')}
          </ButtonFX>
        ) : null}
        {isRecordingAllowed ? (
          <RecordButton
            broadcastStatus={broadcastStatus}
            cardDataUrlDefinition={urlDefinition ?? undefined}
            context={RecordContext.Card}
            displayMode={DisplayMode.Button}
            item={item}
            key='record'
            kind={recordKind}
            location={tvLocationMetadata?.location ?? null}
            previewCatchupScheduledEventId={previewCatchupScheduledEventId}
            programMetadata={programMetadata}
            seriesMetadata={seriesMetadata}
            tileType={tileType}
          />
        ) : null}
        {this.renderWishlistButton()}
      </>
    );
  };

  renderActionPanelAndPricing = (): React.Node => {
    const { cardData } = this.props;
    const { contentType } = this.state;

    if (!cardData) {
      return null;
    }

    switch (contentType) {
      case ItemContent.VODOrDeeplink:
        return this.renderVodActionPanel();

      case ItemContent.TV:
      case ItemContent.NetgemSVOD:
        return this.renderTVActionPanel();

      case ItemContent.Game:
        return this.renderGameActionPanel();

      default:
        return null;
    }
  };

  getBackTitle = (previousCard: CARD_DATA_MODAL_TYPE): ?string => {
    const {
      item: { type },
      programMetadata,
      seriesMetadata,
    } = previousCard;

    const seriesTitle = getTitle(seriesMetadata, Localizer.language);
    const { episodeIndex, episodeTitle } = getEpisodeIndexAndTitle(programMetadata, seriesTitle, Localizer.language);

    const titleArray = [];
    if (seriesTitle) {
      titleArray.push(seriesTitle);
    }
    if (type !== ItemType.Series && episodeTitle) {
      titleArray.push(`${episodeIndex ? `${episodeIndex} : ` : ''}${episodeTitle}`);
    }

    return titleArray.length > 0 ? titleArray.join(' - ') : null;
  };

  renderTagline = (cardData: CARD_DATA_MODAL_TYPE, title: string | null): React.Node => {
    const { contentType, isSeries } = this.state;

    if (contentType !== ItemContent.VODOrDeeplink && contentType !== ItemContent.Game) {
      return null;
    }

    const metadata = isSeries ? this.getSeriesMetadata() : this.getProgramMetadata();
    const tagline = getTagline(metadata, Localizer.language);

    if (!tagline || tagline === title) {
      return null;
    }

    return <div className='tagline'>{tagline}</div>;
  };

  handleErrorPictoOnClick = () => {
    const { failedRecordingId, warningScheduledEventId, warningScheduledRecordingId } = this;

    Messenger.emit(MessengerEvents.OPEN_NPVR_MANAGEMENT_SCREEN, warningScheduledRecordingId, failedRecordingId, warningScheduledEventId);
  };

  renderRecordErrorPicto = (recordOutcome: ?RecordingOutcome, broadcastStatus: BroadcastStatus): React.Node => {
    const { cardData } = this.props;

    if (!cardData || !recordOutcome) {
      return null;
    }

    let description: string | null = null;
    let ariaLabel: string | null = null;
    if (recordOutcome === RecordingOutcome.OutOfQuota) {
      description = Localizer.localize('modal.card.status_picto.warning_out_of_quota');
      ariaLabel = Localizer.localize('accessibility.recordings.errors.out_of_quota');
    } else if (recordOutcome === RecordingOutcome.ExceededConcurrency) {
      description = Localizer.localize('modal.card.status_picto.warning_exceeded_concurrency');
      ariaLabel = Localizer.localize('accessibility.recordings.errors.exceeded_concurrency');
    } else if (recordOutcome === RecordingOutcome.ServerError) {
      description = Localizer.localize('modal.card.status_picto.warning_server');
      ariaLabel = Localizer.localize('accessibility.recordings.errors.server');
    } else {
      return null;
    }

    let pictoElement = null;
    if (broadcastStatus === BroadcastStatus.Past || broadcastStatus === BroadcastStatus.Live) {
      description = Localizer.localize('modal.card.status_picto.failure');
      pictoElement = <PictoFailure className='alertPicto failure' />;
    } else {
      pictoElement = <PictoWarning className='alertPicto warning' />;
    }

    return (
      <button aria-label={ariaLabel} className='recordError' onClick={this.handleErrorPictoOnClick} type='button'>
        {pictoElement}
        <div className='description'>{description}</div>
      </button>
    );
  };

  renderSingleTVProgramInfo = (item: NETGEM_API_V8_FEED_ITEM): React.Node => {
    const { broadcastStatus, contentType, isDeletable, isLiveRecording, isSeries } = this.state;
    const { recordOutcome } = this;

    if (isSeries || contentType !== ItemContent.TV) {
      return null;
    }

    const liveRecordingElement = isLiveRecording ? (
      <div className='liveRecording'>
        <PictoRecord className='recording live' />
        <div className='text'>{Localizer.localize('common.status.live_recording')}</div>
      </div>
    ) : null;

    const recordErrorPicto = this.renderRecordErrorPicto(recordOutcome, broadcastStatus);

    const scheduledRecordingElement =
      !recordErrorPicto && !isLiveRecording && isDeletable && broadcastStatus === BroadcastStatus.Future ? (
        <div className='scheduledRecording'>
          <PictoClock className='scheduledRecording' hasBackground />
          <div className='text'>{Localizer.localize('common.status.scheduled_recording')}</div>
        </div>
      ) : null;

    const { endTime, startTime } = getStartAndEndTimes(item);

    return (
      <div className='programInfo'>
        <StatusPicto item={item} />
        {liveRecordingElement}
        {scheduledRecordingElement}
        {recordErrorPicto}
        {this.renderBroadcastTime(startTime, endTime)}
      </div>
    );
  };

  handleLockOnClick = () => {
    const { cardData, localShowMessage, localShowPinModal } = this.props;
    const {
      parentalControlState: { hasPinCode, lockReason },
    } = this.state;

    if (!hasPinCode && lockReason === ParentalControlLockReason.AdultContent) {
      // Content is locked and cannot be unlocked
      showUnavailableContentModal(localShowMessage);
      return;
    }

    if (hasPinCode) {
      // Ask PIN code
      localShowPinModal({ item: cardData?.item, kind: lockReason === ParentalControlLockReason.AdultContent ? PinModalKind.UnlockAdultContent : PinModalKind.UnlockContent });
    } else {
      // PIN code creation
      localShowPinModal({ kind: PinModalKind.CreateMaster });
    }
  };

  renderLockOverlay = (): React.Node => {
    const { cardData } = this.props;
    const {
      parentalControlState: { lockState },
    } = this.state;

    if (lockState !== ParentalControlLockState.Locked || cardData?.isUnlockedAdultContent === true) {
      return null;
    }

    return (
      <div className='cardLockOverlay' onClick={this.handleLockOnClick}>
        <PictoLock onClick={this.handleLockOnClick} />
      </div>
    );
  };

  getBackgroundElement = (): React.Node => {
    const { backgroundImageUrl, contentType, imageDisplayType, slideshowImages } = this.state;

    if (contentType === ItemContent.Game) {
      // Game
      return (
        <>
          <GameSlideshow imageIds={slideshowImages} title={getTitle(this.getProgramMetadata(), Localizer.language)} />
          <div className='backgroundImage mask' />
        </>
      );
    }

    if (imageDisplayType === ImageDisplayType.Unset || !backgroundImageUrl) {
      return null;
    }

    return (
      <>
        <div className={clsx('backgroundImage', 'image', imageDisplayType === ImageDisplayType.Portrait && 'blurred')} style={{ backgroundImage: `url(${backgroundImageUrl})` }} />
        <div className='backgroundImage mask' />
      </>
    );
  };

  renderChildren = (cardData: CARD_DATA_MODAL_TYPE): React.Node => {
    const { contentType, imageDisplayType, isSeries, portraitImageUrl } = this.state;

    const {
      displayedSectionCount,
      item,
      item: { id },
    } = cardData;
    const programMetadata = this.getProgramMetadata();
    const seriesMetadata = this.getSeriesMetadata();
    const channelImage = this.renderChannelImage();
    const title = getTitle(programMetadata, Localizer.language);
    const { synopsisElement, titleElement } = this.renderTitleAndSynopsis(cardData, title);
    const programInfoElement = this.renderProgramOrSeriesInfo(contentType === ItemContent.VODOrDeeplink);
    const singleTVProgramInfoElement = this.renderSingleTVProgramInfo(item);
    const actionPanel = this.renderActionPanelAndPricing();
    const tagLineElement = this.renderTagline(cardData, title);
    const backgroundElement = this.getBackgroundElement();

    // Fixes missing title when opening app from deeplink, then navigating to a related item
    const localCardData = { ...cardData };
    if (programMetadata !== null && cardData.programMetadata === undefined) {
      localCardData.programMetadata = programMetadata;
    }
    if (seriesMetadata !== null && cardData.seriesMetadata === undefined) {
      localCardData.seriesMetadata = seriesMetadata;
    }

    const coverElement =
      imageDisplayType !== ImageDisplayType.Unset && imageDisplayType === ImageDisplayType.Portrait && portraitImageUrl ? (
        <img alt={Localizer.localize('accessibility.navigation.movie_cover', { title: title ?? '' })} className='cover' onClick={this.handleInfoOnClick} src={portraitImageUrl} />
      ) : null;

    return (
      <div
        className={clsx('slider', (typeof displayedSectionCount !== 'number' || displayedSectionCount > 1) && 'parallax')}
        ref={(instance: HTMLElement | null) => {
          this.slider = instance;
        }}
      >
        {backgroundElement}
        {coverElement}
        <div className='infoAndActionsContainer' onClick={this.handleInfoOnClick}>
          <div className='infoContainer'>
            {channelImage}
            {titleElement}
            <div className='metadataContainer'>
              {singleTVProgramInfoElement}
              <div className='programInfo'>{programInfoElement}</div>
              {this.renderGameInfo()}
              <CreditsView metadata={isSeries && seriesMetadata ? seriesMetadata : programMetadata} />
              {tagLineElement}
              {synopsisElement}
              {this.renderGameCopyright()}
            </div>
          </div>
          <div className='iconBar'>{actionPanel}</div>
        </div>
        <CardAvenue cardData={localCardData} displayedSectionCount={displayedSectionCount} key={id} />
        {this.renderLockOverlay()}
      </div>
    );
  };

  render(): React.Node {
    const { cardData } = this.props;
    const { imageDisplayType } = this.state;

    if (!cardData) {
      return null;
    }

    const { previousCard, tileType } = cardData;

    return (
      <Modal
        backCallback={previousCard ? this.openPreviousCard : undefined}
        className={clsx('card', getDisplayType(imageDisplayType, tileType))}
        title={previousCard ? this.getBackTitle(previousCard) : undefined}
      >
        {this.renderChildren(cardData)}
      </Modal>
    );
  }
}

const mapStateToProps = (state: CombinedReducers): ReduxCardModalReducerStateType => {
  return {
    appConfiguration: state.appConfiguration,
    applicationName: state.appConfiguration.applicationName,
    channels: state.appConfiguration.deviceChannels,
    commercialOffers: state.appConfiguration.rightsCommercialOffers,
    deeplink: state.ui.deeplink,
    defaultRights: state.appConfiguration.rightsDefault,
    favoriteList: state.ui.favoriteList || [],
    isDebugModeEnabled: state.appConfiguration.isDebugModeEnabled,
    isNpvrEnabled: state.appConfiguration.features[FEATURE_NPVR] && state.npvr.isEnabled,
    isRegisteredAsGuest: state.appRegistration.registration === RegistrationType.RegisteredAsGuest,
    isSubscriptionFeatureEnabled: state.appConfiguration.features[FEATURE_SUBSCRIPTION],
    npvrRecordingsFuture: state.npvr.npvrRecordingsFuture,
    npvrRecordingsList: state.npvr.npvrRecordingsList,
    npvrScheduledRecordingsList: state.npvr.npvrScheduledRecordingsList,
    playingHistory: PlayingHistoryCache.checkGet(state.ui.playingHistory, state.ui.playingHistoryStatus, true),
    playingHistoryStatus: state.ui.playingHistoryStatus,
    purchaseList: state.ui.purchaseList,
    streamPriorities: state.appConfiguration.playerStreamPriorities,
    userRights: state.appConfiguration.rightsUser,
    viewingHistory: state.ui.viewingHistory,
    viewingHistoryStatus: state.ui.viewingHistoryStatus,
    wishlist: WishlistCache.checkGet(state.ui.wishlist, state.ui.wishlistStatus).ids,
    wishlistStatus: state.ui.wishlistStatus,
  };
};

const mapDispatchToProps = (dispatch: Dispatch): ReduxCardModalDispatchToPropsType => {
  return {
    localAddToFavoriteList: (titId: string, signal?: AbortSignal): Promise<void> => dispatch(addToFavoriteList(titId, signal)),

    localAddToWishlist: (assetId: string, channelId: string, signal?: AbortSignal): Promise<void> => dispatch(addToWishlist(assetId, channelId, signal)),

    localGetGamestreamToken: (item: NETGEM_API_V8_FEED_ITEM, signal?: AbortSignal): Promise<GamestreamToken> => dispatch(getGamestreamToken(item, signal)),

    localGetImageSourceUrl: (assetId: string, width: number, height: number): Promise<string> => dispatch(getImageSourceUrl(assetId, width, height)),

    localGetImageUrl: (assetId: string, width: number, height: number, theme?: ThemeType, signal?: AbortSignal): Promise<string> =>
      dispatch(
        getImageUrl(
          {
            assetId,
            height,
            theme,
            width,
          },
          signal,
        ),
      ),

    localGetPurchaseInfoPerAsset: (id: string, channelId: string, signal?: AbortSignal): Promise<NETGEM_API_V8_PURCHASE_INFO> => dispatch(getPurchaseInfoPerAsset(id, channelId, signal)),

    localGetVodLocations: (locations: Array<NETGEM_API_V8_ITEM_LOCATION>, signal?: AbortSignal): Promise<Array<NETGEM_API_V8_METADATA_SCHEDULE>> => dispatch(getVodLocations(locations, signal)),

    localHideModal: (shouldBeReopenedLater?: boolean): void => dispatch(hideModal(shouldBeReopenedLater)),

    localRemoveFromFavoriteList: (titId: string, signal?: AbortSignal): Promise<void> => dispatch(removeFromFavoriteList(titId, signal)),

    localRemoveFromWishlist: (assetId: string, channelId: string, signal?: AbortSignal): Promise<void> => dispatch(removeFromWishlist(assetId, channelId, signal)),

    localReopenModalCard: (): void => dispatch(reopenModalCard()),

    localResetSectionPageIndices: (prefix?: string): void => dispatch(resetSectionPageIndices(prefix)),

    localSendV8LocationCatchupForAssetRequest: (assetId: string, startDate: number, range: number, signal?: AbortSignal): Promise<NETGEM_API_V8_REQUEST_RESPONSE> =>
      dispatch(sendV8LocationCatchupForAssetRequest(assetId, startDate, range, undefined, signal)),

    localSendV8MetadataLocationRequest: (assetId: string, signal?: AbortSignal): Promise<NETGEM_API_V8_REQUEST_RESPONSE> => dispatch(sendV8MetadataLocationRequest(assetId, signal)),

    localSendV8MetadataRequest: (assetId: string, type: MetadataKind, signal?: AbortSignal): Promise<NETGEM_API_V8_METADATA> => dispatch(sendV8MetadataRequest(assetId, type, signal)),

    localSendV8RecordingsMetadataRequest: (recordId: string, signal?: AbortSignal): Promise<NETGEM_API_V8_REQUEST_RESPONSE> => dispatch(sendV8RecordingsMetadataRequest(recordId, signal)),

    localShowConfirmation: (data: CONFIRMATION_DATA_MODAL_TYPE, ariaLabel: string): void => dispatch(showConfirmationModal(data, ariaLabel)),

    localShowEula: (data: EULA_DATA_MODAL_TYPE): void => dispatch(showEulaModal(data)),

    localShowGameProfileSelect: (data: GAME_PROFILE_SELECT_DATA_MODAL_TYPE): void => dispatch(showGameProfileSelectModal(data)),

    localShowMessage: (messageData: MESSAGE_MODAL_TYPE, ariaLabel?: string): void => dispatch(showMessageModal(messageData, ariaLabel)),

    localShowPinModal: (data: PIN_DATA_MODAL_TYPE): void => dispatch(showPinModal(data)),

    localUpdatePlayingHistory: (playingHistoryItem: NETGEM_API_PLAYINGHISTORY_ITEM, itemId: string): Promise<void> => dispatch(updatePlayingHistory(playingHistoryItem, itemId)),

    showVodPurchaseDialog: (vodPurchaseData: VOD_PURCHASE_DATA_TYPE): void => dispatch(showVodPurchaseModal(vodPurchaseData)),
  };
};

const CardModal: React.ComponentType<ModalState> = connect(mapStateToProps, mapDispatchToProps)(CardModalView);

export default CardModal;
